'use client';

import { useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import './home-mobile.scss';
import LoadingCapabilities from "./LoadingCapabilities";
import Intro from "./Intro";
import Reach from "./Reach";
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/scrollbar';
import { PAGE_MOBILE } from "@/lib/constants";
import { Swiper, SwiperSlide } from "swiper/react";
import { ProjectModel } from "@/lib/models/Project";
import { usePathname } from "next/navigation";
import ProjectMobileItem from "./ProjectMobileItem";
import FooterMobile from "@/components/Mobile/FooterMobile";
import dynamic from "next/dynamic";
import { RouterName } from "@/lib/enum/RouterEnum";
import { getNavState, setNavState } from "@/lib/utils/navState";
import { useTransitionExit } from "@/components/Mobile/Transition/SwipeTransition";
import TransitionContext from "@/components/Mobile/Transition/TransitionContext";
import MobileShellContext from "@/components/Mobile/MobileShellContext";

const Title = dynamic(() => import("./Title"), { ssr: false });

// Run before paint on the client, but fall back to useEffect during SSR so React
// doesn't warn about useLayoutEffect on the server.
const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

interface Props {
  projects: ProjectModel[];
}

const HomeMobile = ({ projects }: Props) => {
  const swiperRef = useRef<any>(null);
  const loadingBorderResetTimerRef = useRef<number | null>(null);
  const loadingToTitleDragRef = useRef(false);
  const pathname = usePathname();
  const exit = useTransitionExit();
  const { markRouteReady } = useContext(TransitionContext);
  const { setCurrentSlide, pendingSlide, clearPendingSlide } = useContext(MobileShellContext);
  // Start on TITLE on both server and client so SSR markup matches the first
  // client render (sessionStorage is client-only). The saved slide is restored
  // below in an isomorphic layout effect, before the browser paints.
  const [currentSlideIndex, setCurrentSlideIndex] = useState<number | null>(PAGE_MOBILE.TITLE);
  const [footer, setFooter] = useState(PAGE_MOBILE.TITLE);
  const [windowHeight, setWindowHeight] = useState(0);
  const [swiperReady, setSwiperReady] = useState(false);
  const [restorationApplied, setRestorationApplied] = useState(false);
  const [loadingBorderOffset, setLoadingBorderOffset] = useState<number | null>(null);

  const _getCurrentProject = () => {
    if (currentSlideIndex === null) {
      return undefined;
    }

    const projectIndex = currentSlideIndex - PAGE_MOBILE.PROJECTS;
    if (projectIndex < 0 || projectIndex >= projects.length) {
      return undefined;
    }

    return projects[projectIndex];
  }

  // Navigates to project details of current project.
  const _changeShowDetail = () => {
    const project = _getCurrentProject();
    if (project) {
      if (currentSlideIndex !== null) setNavState(currentSlideIndex);
      exit(`${RouterName.projectDetail}/${project.slug}`);
    }
  };

  // Restore the saved slide state before the browser paints, so the header/footer
  // never flash the title-slide state on the way back from a project detail.
  useIsomorphicLayoutEffect(() => {
    const saved = getNavState<number>();
    if (typeof saved === 'number' && saved !== PAGE_MOBILE.TITLE) {
      setCurrentSlideIndex(saved);
      setFooter(saved);
    }
  }, []);

  // Slide to the slide we were at before we navigated into project details.
  useEffect(() => {
    const saved = getNavState<number>();
    if (typeof saved === 'number') {
      swiperRef.current?.slideToLoop(saved, 0);
    }
    setRestorationApplied(true);
  }, [pathname]);

  useEffect(() => {
    if (swiperReady && restorationApplied) {
      markRouteReady(pathname);
    }
  }, [swiperReady, restorationApplied, pathname, markRouteReady]);

  // Push the slide index to the shell context before paint so the header's
  // SOUND OFF button (gated on currentSlide === TITLE) never flashes during
  // restoration on the way back from a project detail or a hard reload.
  useIsomorphicLayoutEffect(() => {
    if (currentSlideIndex !== null) {
      setCurrentSlide(currentSlideIndex);
    }
  }, [currentSlideIndex, setCurrentSlide]);

  // Apply a slide-jump request from the directory modal even when pathname
  // didn't change (e.g. user was already on `/` when they picked an item).
  useEffect(() => {
    if (pendingSlide === null) return;
    if (!swiperReady) return;
    swiperRef.current?.slideToLoop(pendingSlide, 0);
    clearPendingSlide();
  }, [pendingSlide, swiperReady, clearPendingSlide]);

  const _handleNextStep = () => {
    if (swiperRef.current) {
      swiperRef.current.slideNext();
    }
  };

  const clearLoadingBorderTimer = () => {
    if (loadingBorderResetTimerRef.current !== null) {
      window.clearTimeout(loadingBorderResetTimerRef.current);
      loadingBorderResetTimerRef.current = null;
    }
  };

  const commitSwiperState = (swiper: any) => {
    if (!swiper || swiper.realIndex === currentSlideIndex) return;

    setCurrentSlideIndex(swiper.realIndex);
    setFooter(swiper.realIndex);
    setNavState(swiper.realIndex);
  };

  const resetLoadingBorder = (swiper?: any) => {
    clearLoadingBorderTimer();
    loadingToTitleDragRef.current = false;
    setLoadingBorderOffset(null);

    if (swiper) {
      swiper.allowTouchMove = swiper.realIndex !== PAGE_MOBILE.TITLE;
      commitSwiperState(swiper);
    }
  };

  const getLoadingSlideProgress = (swiper: any) => {
    const activeSlide = swiper.slides?.[swiper.activeIndex];
    if (activeSlide?.getAttribute('data-swiper-slide-index') === String(PAGE_MOBILE.LOADING)) {
      return activeSlide.progress ?? 0;
    }

    const loadingSlides = (Array.from(swiper.slides ?? []) as any[])
      .filter((slide: any) => slide.getAttribute('data-swiper-slide-index') === String(PAGE_MOBILE.LOADING));

    if (!loadingSlides.length) return 0;

    return loadingSlides.reduce((closest: any, slide: any) => {
      return Math.abs(slide.progress ?? 0) < Math.abs(closest.progress ?? 0) ? slide : closest;
    }, loadingSlides[0]).progress ?? 0;
  };

  const syncLoadingBorder = (swiper: any) => {
    if (!loadingToTitleDragRef.current) return;

    const progress = getLoadingSlideProgress(swiper);
    const width = swiper.width || window.innerWidth;
    const offset = Math.max(0, Math.min(width, -progress * width));

    setLoadingBorderOffset(offset);
  };

  const handleSliderMove = (swiper: any) => {
    const diff = swiper.touches?.diff ?? 0;
    const isLoadingToTitleDrag = swiper.realIndex === PAGE_MOBILE.LOADING && diff > 0;

    if (!isLoadingToTitleDrag) {
      if (!loadingToTitleDragRef.current) resetLoadingBorder(swiper);
      return;
    }

    clearLoadingBorderTimer();
    loadingToTitleDragRef.current = true;
    syncLoadingBorder(swiper);
  };

  const handleTouchEnd = (swiper: any) => {
    if (!loadingToTitleDragRef.current) return;

    syncLoadingBorder(swiper);
    clearLoadingBorderTimer();
    loadingBorderResetTimerRef.current = window.setTimeout(() => resetLoadingBorder(swiper), 450);
  };

  const changeSwiper = (swiper: any) => {
    if (loadingToTitleDragRef.current) return;

    // GX: Added if check to skip unneccessary re-renders
    commitSwiperState(swiper);
  };

  const _handlePrevStep = () => {
    if (swiperRef.current) {
      swiperRef.current.slidePrev();
    }
  };

  useEffect(() => {
    const handleResize = () => setWindowHeight(window.innerHeight);

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);


  const isTitleSlide = currentSlideIndex === PAGE_MOBILE.TITLE;

  const _renderProjectItem = (item: ProjectModel, index: number) => {
    return (
      <SwiperSlide 
        virtualIndex={index + PAGE_MOBILE.PROJECTS} 
        key={index + PAGE_MOBILE.PROJECTS}
      >
        <ProjectMobileItem
          height={windowHeight}
          index={index}
          project={item}
          goToDetail={_changeShowDetail}
          realIndex={currentSlideIndex}
        />
      </SwiperSlide>
    )
  };

  const _renderFooter = () => {
    return (
      <FooterMobile
        footerType={footer}
        footerColor={_getCurrentProject()?.mobileAccentColor}
        handlePrevStep={_handlePrevStep}
        handleNextStep={_handleNextStep}
        onViewWork={_changeShowDetail}
        listProject={projects}
        hideBorder={loadingBorderOffset !== null}
        hideText={loadingBorderOffset !== null}
      />
    )
  };

  useEffect(() => {
    return () => clearLoadingBorderTimer();
  }, []);

  return (
    <div className={`home-intro-mobile ${isTitleSlide ? 'is-title-slide' : ''}`}>
      <Swiper
        spaceBetween={0}
        slidesPerView={1}
        className='swiper-container'
        ref={swiperRef}
        onSwiper={(swiper: any) => {
          swiperRef.current = swiper;
          const saved = getNavState<number>();
          if (typeof saved === 'number' && saved !== PAGE_MOBILE.TITLE) {
            swiper.slideToLoop(saved, 0);
          }
          swiper.allowTouchMove = swiper.realIndex !== PAGE_MOBILE.TITLE;
          setSwiperReady(true);
        }}
        onSlideChange={changeSwiper}
        onProgress={syncLoadingBorder}
        onSliderMove={handleSliderMove}
        onTouchEnd={handleTouchEnd}
        onTransitionEnd={resetLoadingBorder}
        initialSlide={PAGE_MOBILE.TITLE}
        loop
        watchSlidesProgress
        onActiveIndexChange={(swiper: any) => {
          if (loadingToTitleDragRef.current) return;

          swiper.allowTouchMove = swiper.realIndex !== PAGE_MOBILE.TITLE;
        }}
      >
        <SwiperSlide virtualIndex={PAGE_MOBILE.TITLE} className="title-panel">
          <Title
            key={PAGE_MOBILE.TITLE}
            handleNextStep={_handleNextStep}
            isActive={isTitleSlide}
          />
        </SwiperSlide>
        <SwiperSlide virtualIndex={PAGE_MOBILE.LOADING}>
          <LoadingCapabilities key={PAGE_MOBILE.LOADING} />
        </SwiperSlide>
        <SwiperSlide virtualIndex={PAGE_MOBILE.INTRO}>
          <Intro key={PAGE_MOBILE.INTRO} />
        </SwiperSlide>
        {projects.map((item, index) => {
          return _renderProjectItem(item, index)
        })}
        <SwiperSlide virtualIndex={PAGE_MOBILE.REACH + projects.length}>
          <Reach
            key={PAGE_MOBILE.REACH}
            footerType={PAGE_MOBILE.REACH}
          />
        </SwiperSlide>
      </Swiper>
      {loadingBorderOffset !== null && (
        <div
          className="loading-footer-follow"
          style={{ transform: `translate3d(${loadingBorderOffset}px, 0, 0)` }}
        >
          <div className="footer-mobile-border" />
        </div>
      )}
      {_renderFooter()}
    </div>
  )
};

export default HomeMobile;
